/**
 * The HDR → SDR mapping from tonemap-gl.ts's fragment shader, on the CPU.
 * The HDR explainer on Wave's pages uses it to show exactly what the GPU
 * path does, without loading the WebGL code. Keep it in step with
 * FRAGMENT_SHADER in tonemap-gl.ts.
 */

const PQ = { m1: 0.1593017578125, m2: 78.84375, c1: 0.8359375, c2: 18.8515625, c3: 18.6875 };
const HLG = { a: 0.17883277, b: 0.28466892, c: 0.55991073 };
const REFERENCE_WHITE_NITS = 203;
const KNEE = 0.6;

/** The shader's mapping for one BT.2020 R'G'B' signal (0-1 per channel),
 *  on the CPU, returning display-ready RGB (0-1, gamma 2.4). The HDR
 *  explainer uses it; keep it in step with FRAGMENT_SHADER. */
export function toneMapSignal(signal: readonly [number, number, number], transfer: 'pq' | 'hlg'): [number, number, number] {
  let nits: number[];
  if (transfer === 'pq') {
    nits = signal.map((e) => {
      const p = Math.pow(Math.max(e, 0), 1 / PQ.m2);
      return 10000 * Math.pow(Math.max(p - PQ.c1, 0) / (PQ.c2 - PQ.c3 * p), 1 / PQ.m1);
    });
  } else {
    const s = signal.map((e) => (e <= 0.5 ? (e * e) / 3 : (Math.exp((e - HLG.c) / HLG.a) + HLG.b) / 12));
    const ys = 0.2627 * s[0] + 0.678 * s[1] + 0.0593 * s[2];
    const gain = 1000 * Math.pow(Math.max(ys, 1e-6), 0.2);
    nits = s.map((v) => gain * v);
  }
  const rgb = [
    1.6605 * nits[0] - 0.5876 * nits[1] - 0.0728 * nits[2],
    -0.1246 * nits[0] + 1.1329 * nits[1] - 0.0083 * nits[2],
    -0.0182 * nits[0] - 0.1006 * nits[1] + 1.1187 * nits[2],
  ].map((v) => Math.max(v, 0) / REFERENCE_WHITE_NITS);
  const m = Math.max(...rgb);
  const mapped = m <= KNEE ? m : KNEE + (1 - KNEE) * (1 - Math.exp(-(m - KNEE) / (1 - KNEE)));
  const scale = m > 0 ? mapped / m : 0;
  const out = rgb.map((v) => Math.pow(Math.min(1, Math.max(0, v * scale)), 1 / 2.4));
  return [out[0], out[1], out[2]];
}
